import React, { useMemo } from 'react';
import {
    Footer as LayoutComponentFooter
} from '@vtblife/layout-components';
import { errorCatcherWrap } from '@search/error/src/catcher';
import { MenuItemComponentProps } from '@vtblife/layout-components/dist/types/menu-item.interface';
import {
    FooterApp,
    FooterLink,
    FooterMiddleCol,
    FooterPhoneType,
    FooterSocialLink,
} from '@vtblife/layout-components/dist/types/footer.interface';
import { FooterLink as FooterLinkConfig } from '@vtblife/layout-config/dist/types';
import { IconSmallSize, IconMediumSize } from '@vtblife/uikit-icons/dist/types';

import { graphql, useGql } from '@search/gql-client/src';
import type { FooterQuery$data as FooterQueryResponse, FooterQuery$variables as FooterQueryVariables } from './__generated__/FooterQuery.graphql';

const ALLOWED_SOCIAL_ICONS: ReadonlyArray<IconSmallSize & IconMediumSize> = [ 'vk', 'telegram', 'max' ];

const isAllowedSocialIcon = (icon: string): icon is IconSmallSize & IconMediumSize =>
    ALLOWED_SOCIAL_ICONS.some(allowed => allowed === icon);

const query = graphql`
    query FooterQuery($regionId: Int) {
        footer(regionId: $regionId) {
            downloadAppQrCode
            socialLinks {
                name
                icon
                href
            }
            apps {
                href
                imgSrc
                analytics
            }
            top {
                to
                name
                href
                cdn
                analytics
                target
            }
            middleLinks {
                title
                links {
                    to
                    href
                    cdn
                    name
                    analytics
                    target
                    linkGenerationId
                }
            }
        }
    }
`;

const FooterMenuItemComponent: React.FC<MenuItemComponentProps> = props => {
    const { url = '/', path, ...rest } = props;

    return <a key={url} href={url === 'undefined' ? path : url} {...rest} />;
};

const Footer: React.FC<{
    buildLink?: (url: string) => string;
    phone?: FooterPhoneType;
    regionId?: number;
}> = ({ regionId, ...props }) => {
    const { data } = useGql<FooterQueryVariables, FooterQueryResponse>(query, { regionId });

    const { top, middleLinks: rawMiddleLinks, downloadAppQrCode } = data?.footer || {};
    const rawSocialLinks = data?.footer?.socialLinks;
    const rawApps = data?.footer?.apps;

    const socialLinks = useMemo<FooterSocialLink[]>(() => (rawSocialLinks ?? [])
        .filter((link): link is { name: string; icon: IconSmallSize & IconMediumSize; href: string } =>
            link !== null && link !== undefined && isAllowedSocialIcon(link.icon))
        .map(({ name, icon, href }) => ({ name, icon, href }))
    , [ rawSocialLinks ]);

    const apps = useMemo<FooterApp[]>(() => (rawApps ?? [])
        .map(({ href, imgSrc, analytics }) => ({ href, imgSrc, analytics: analytics ?? undefined }))
    , [ rawApps ]);

    const buildLink = (link: FooterLinkConfig) => {
        const url = (link.to || link.href) as string;
        const absoluteUrl = props.buildLink ? props.buildLink(url) : url;

        return {
            ...link,
            href: link.cdn ? url : absoluteUrl
        };
    };

    const topLinks: Array<Array<FooterLink>> = (top || [])
        .map(links => (links || [])
            .map(link => buildLink(link as FooterLinkConfig))
        );

    const middleLinks: Array<FooterMiddleCol> = (rawMiddleLinks || [])
        .map(column => ({
            title: column!.title,
            links: (column!.links || []).map(link => buildLink(link as FooterLinkConfig))
        }));

    return (
        <LayoutComponentFooter
            top={topLinks}
            middle={middleLinks}
            menuItemComponent={FooterMenuItemComponent}
            phone={props.phone}
            socialLinks={socialLinks}
            downloadAppQrCode={downloadAppQrCode ?? ''}
            apps={apps}
        />
    );
};

export default errorCatcherWrap(Footer);
